import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import Hls from "hls.js";

/**
 * `<HlsVideo>` — the shared video playback primitive for M5.
 *
 * Wraps a `<video>` element and runs the source-loading ladder
 * ported from legacy `js/player.js:loadPlaybackSource`:
 *
 *   1. **Native HLS** (Safari / iOS) — `video.canPlayType(
 *      'application/vnd.apple.mpegurl')` is truthy → set `src` to
 *      the `.m3u8` directly; a one-shot `error` listener falls
 *      back to MP4.
 *   2. **hls.js** (Chrome / Firefox / Edge) — `Hls.isSupported()`
 *      → attach an `Hls` instance, `loadSource` on MEDIA_ATTACHED,
 *      fall back to MP4 on a *fatal* `ERROR`.
 *   3. **MP4 fallback** — no HLS path available, or any of the
 *      above errored → set `src` to the direct-download MP4.
 *
 * **Race guard.** Every `src` (re)load bumps a `playRequestToken`
 * ref. The native-HLS and hls.js callbacks capture the token at
 * load time and no-op if it's stale — so a fast match/clip swap
 * can't have an old load's fallback or `loadSource` fire against
 * the new source. Mirrors legacy `_playRequestToken`.
 *
 * **Ref.** Forwards a ref to the underlying `<video>` element so
 * callers (Coach Review's timestamp readout + keyboard scrubbing,
 * the heartbeat, the focused-modal clip-mode boundary watcher)
 * can read `currentTime` and attach their own listeners. The
 * component owns ONLY source loading + teardown; playback control
 * is the caller's.
 *
 * The hls.js instance is destroyed on `src` change and on unmount.
 */

export type HlsVideoProps = {
  /** HLS playlist URL (`.m3u8`). Build via `getStreamUrls`. */
  hlsUrl: string;
  /** Optional native-HLS-only playlist URL.
   *
   * WebKit AirPlay is most reliable when the video element owns a single
   * media playlist before the picker opens. hls.js browsers still use
   * `hlsUrl`, preserving adaptive playback outside native WebKit. */
  nativeHlsUrl?: string;
  /** Direct-download MP4 URL — the fallback when HLS can't load.
   * For LIVE streams pass an empty string — there's no MP4 fallback
   * for a live feed; `onUnavailable` fires instead. */
  mp4Url: string;
  /** Forwarded to the `<video>`. Default: native controls shown. */
  controls?: boolean;
  className?: string;
  /** Prefer the MP4 fallback on native WebKit HLS browsers. Used by public
   * VOD playback so AirPlay receives a stable direct MP4 source from initial
   * load instead of a multivariant HLS master that some TVs reject during
   * handoff. hls.js browsers and live streams ignore this. */
  preferMp4OnNativeHls?: boolean;
  /** Called once when neither HLS nor MP4 can play — lets the
   * caller render a "video unavailable" state. */
  onUnavailable?: () => void;
  /** Live-stream mode (M11b). Switches the hls.js config from the
   * VOD-tuned defaults (long back-buffer, generous max-buffer) to
   * the LL-HLS profile from legacy `js/live.js`:
   *   - `backBufferLength: 30`   — short backbuffer (live, not VOD)
   *   - `maxBufferLength: 90`    — pre-buffer enough that one slow
   *     segment from a weak-uplink camera doesn't drain us
   *   - `liveSyncDurationCount: 6`  — sit 6 segments back from live
   *   - `liveMaxLatencyDurationCount: 12` — resync ceiling
   *
   * Also forces the native-HLS branch on Safari/iOS so AirPlay's
   * `webkitShowPlaybackTargetPicker` surfaces (MSE-backed video
   * doesn't fire AirPlay availability events). Pre-fork legacy
   * comment block in `js/live.js:135-150`. */
  live?: boolean;
  /** Best-effort autoplay after source readiness — fires
   * `video.play().catch(() => {})` on `loadedmetadata` (native
   * HLS) or `MANIFEST_PARSED` (hls.js). Use for live streams so
   * the viewer doesn't land on a paused player when the feed is
   * already playable; VOD callers leave it false so the viewer's
   * scrub position is respected (M5 / M11a). Promise rejection
   * is swallowed — browser autoplay policy may block playback
   * until a user gesture, which is the expected fallback. */
  autoPlay?: boolean;
  "data-testid"?: string;
};

type WebkitAirPlayVideo = HTMLVideoElement & {
  webkitShowPlaybackTargetPicker?: () => void;
};

function supportsNativeHls(el: HTMLVideoElement): boolean {
  return !!el.canPlayType("application/vnd.apple.mpegurl");
}

function hasAppleRemotePlaybackApi(el: HTMLVideoElement): boolean {
  const webkitVideo = el as WebkitAirPlayVideo;
  const globalWithAirPlay = globalThis as typeof globalThis & {
    WebKitPlaybackTargetAvailabilityEvent?: unknown;
  };
  return (
    typeof webkitVideo.webkitShowPlaybackTargetPicker === "function" ||
    typeof globalWithAirPlay.WebKitPlaybackTargetAvailabilityEvent !==
      "undefined"
  );
}

export const HlsVideo = forwardRef<HTMLVideoElement, HlsVideoProps>(
  function HlsVideo(
    { hlsUrl, nativeHlsUrl, mp4Url, controls = true, className, preferMp4OnNativeHls = false, onUnavailable, live = false, autoPlay = false, ...rest },
    ref,
  ) {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    // Forward the inner <video> to the caller's ref while keeping
    // our own handle for the loading effect.
    useImperativeHandle(ref, () => videoRef.current as HTMLVideoElement, []);

    // Monotonic token — bumped on every (re)load so stale async
    // callbacks (native-HLS error, hls.js MEDIA_ATTACHED/ERROR)
    // can detect they belong to a superseded source and bail.
    const playRequestToken = useRef(0);

    useEffect(() => {
      // Capture a non-null local — the early return below narrows
      // it, and `cleanup` (a hoisted closure) needs the narrowing
      // to hold at its definition point.
      const video = videoRef.current;
      if (!video) return;
      const el: HTMLVideoElement = video;
      el.disableRemotePlayback = false;
      el.setAttribute("x-webkit-airplay", "allow");

      const token = ++playRequestToken.current;
      let hls: Hls | null = null;
      // The one-shot native-HLS error listener — tracked so the
      // cleanup can remove it if it never fired.
      let nativeErrorHandler: (() => void) | null = null;

      const useMp4Fallback = () => {
        // Stale load — a newer src superseded this one. Don't touch
        // the video element.
        if (token !== playRequestToken.current) return;
        if (hls) {
          hls.destroy();
          hls = null;
        }
        if (nativeErrorHandler) {
          el.removeEventListener("error", nativeErrorHandler);
          nativeErrorHandler = null;
        }
        // Live streams have no MP4 fallback — a live feed isn't a
        // file. Surface the failure to the caller so it can render
        // the offline placeholder.
        if (live || !mp4Url) {
          onUnavailable?.();
          return;
        }
        el.onerror = () => {
          if (token !== playRequestToken.current) return;
          onUnavailable?.();
        };
        el.src = mp4Url;
        el.load();
      };

      // (1) Native HLS — Safari / iOS only.
      //
      // CRITICAL: `canPlayType('application/vnd.apple.mpegurl')` is NOT
      // a reliable "this browser plays HLS" probe. Chrome 1xx returns
      // the truthy string `"maybe"` for the HLS MIME type even though
      // Chrome CANNOT demux HLS natively — assigning a `.m3u8` straight
      // to `video.src` there just stalls forever at readyState 0 with
      // no error event (the player spins; no fallback fires). So we
      // must NOT branch on `canPlayType` alone.
      //
      // The reliable "avoid native HLS in Chrome" signal is
      // `Hls.isSupported()`: true wherever MSE is available
      // (Chrome / Firefox / Edge / desktop Safari), false on iOS Safari
      // where MSE-for-HLS isn't. But Apple AirPlay needs the direct
      // `.m3u8` `video.src` path: MSE-backed hls.js playback on desktop
      // Safari does not reliably surface playback-target availability or
      // hand off to AVPlayer. So native HLS is allowed when hls.js cannot
      // run OR when WebKit exposes its AirPlay remote-playback API.
      //
      // That keeps Chrome's truthy `canPlayType("...mpegurl")` lie on
      // the hls.js branch while restoring the pre-rearch Safari/AirPlay
      // behavior for both VOD and live.
      const nativeHlsOnly =
        supportsNativeHls(el) &&
        (!Hls.isSupported() || hasAppleRemotePlaybackApi(el));
      let autoPlayLoadedHandler: (() => void) | null = null;
      if (!live && preferMp4OnNativeHls && nativeHlsOnly && mp4Url) {
        el.onerror = () => {
          if (token !== playRequestToken.current) return;
          onUnavailable?.();
        };
        el.src = mp4Url;
        el.load();
        return cleanup;
      }
      if (nativeHlsOnly) {
        nativeErrorHandler = useMp4Fallback;
        el.addEventListener("error", nativeErrorHandler, { once: true });
        if (autoPlay) {
          // Legacy `js/live.js:154-158` fires `play().catch(() => {})`
          // after `loadedmetadata` so the live feed starts as soon
          // as the manifest is ready when browser autoplay policy
          // allows it. Promise rejection (user gesture required)
          // is swallowed — the controls remain and the viewer can
          // tap play manually.
          autoPlayLoadedHandler = () => {
            if (token !== playRequestToken.current) return;
            void el.play?.().catch(() => {
              /* autoplay blocked by browser policy — expected fallback */
            });
          };
          el.addEventListener("loadedmetadata", autoPlayLoadedHandler, {
            once: true,
          });
        }
        el.src = nativeHlsUrl || hlsUrl;
        el.load();
        return cleanup;
      }

      // (2) hls.js — Chrome / Firefox / Edge. Config branches on
      // `live`: VOD uses the long-backbuffer M5 defaults; live
      // uses the LL-HLS profile from legacy js/live.js.
      if (Hls.isSupported()) {
        hls = new Hls(
          live
            ? {
                enableWorker: true,
                backBufferLength: 30,
                maxBufferLength: 90,
                liveSyncDurationCount: 6,
                liveMaxLatencyDurationCount: 12,
              }
            : {
                enableWorker: true,
                backBufferLength: 90,
                maxBufferLength: 60,
                maxMaxBufferLength: 120,
              },
        );
        hls.attachMedia(el);
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
          if (token !== playRequestToken.current) return;
          hls?.loadSource(hlsUrl);
        });
        if (autoPlay) {
          // Legacy `js/live.js:199-201` fires `play().catch(() => {})`
          // after `MANIFEST_PARSED` so the live feed starts as soon
          // as hls.js has the manifest. Same Promise-rejection
          // swallow as the native path.
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            if (token !== playRequestToken.current) return;
            void el.play?.().catch(() => {
              /* autoplay blocked by browser policy — expected fallback */
            });
          });
        }
        hls.on(Hls.Events.ERROR, (_event, data) => {
          // Non-fatal errors are recoverable — hls.js handles them
          // internally. Only a fatal error means HLS is dead.
          if (!data.fatal) return;
          useMp4Fallback();
        });
        return cleanup;
      }

      // (3) No HLS path at all — go straight to MP4.
      useMp4Fallback();
      return cleanup;

      function cleanup() {
        // Bump the token so any in-flight callback for THIS load
        // sees itself as stale and no-ops.
        playRequestToken.current++;
        if (hls) {
          hls.destroy();
          hls = null;
        }
        if (nativeErrorHandler) {
          el.removeEventListener("error", nativeErrorHandler);
          nativeErrorHandler = null;
        }
        if (autoPlayLoadedHandler) {
          el.removeEventListener("loadedmetadata", autoPlayLoadedHandler);
          autoPlayLoadedHandler = null;
        }
        el.onerror = null;
      }
    }, [hlsUrl, nativeHlsUrl, mp4Url, preferMp4OnNativeHls, onUnavailable, live, autoPlay]);

    return (
      <video
        ref={videoRef}
        controls={controls}
        playsInline
        className={className}
        data-testid={rest["data-testid"]}
      />
    );
  },
);
